import { Box, Button, Grid, Link, Paper, TablePagination, Typography } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import cx from 'classnames';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { CrownIcon } from '../../../assets/Svg';
import { getProfileImageUpdatedHistories } from '../../../services/api/Moderation';
import settings from '../../../services/settings';
import { IProfileImage, ProfileImageReviewStatus } from '../../../services/types';
import useStyles from './ProfileImageDetail.styles';

interface IProps {
  profileImage?: IProfileImage;
  onClose: () => void;
}

const RowsPerPage = 10;

const ProfileImageDetail = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { profileImage, onClose } = props;

  const [updatedHistories, setUpdatedHistories] = useState<IProfileImage[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  const userId = profileImage?.userId;
  const username = profileImage?.user?.username;
  const displayName = profileImage?.user?.displayName;
  const isWriter = profileImage?.user?.isWriter || false;
  const createdAt = profileImage?.createdAt;
  const reviewStatus = profileImage?.review.status;
  const reportedCount = profileImage?.reportedCount;

  const handlePageChange = useCallback((e: unknown, page: number) => {
    setPage(page);
  }, []);

  const fetchUpdatedHistories = useCallback(async () => {
    if (!userId) {
      return;
    }

    const { data } = await getProfileImageUpdatedHistories(userId, page + 1, RowsPerPage);

    setUpdatedHistories(data.items);
    setTotalCount(data?.totalCount || 0);
  }, [userId, page]);

  useEffect(() => {
    fetchUpdatedHistories();
  }, [fetchUpdatedHistories]);

  return (
    <Paper className={classes.wrap}>
      <Box className={classes.detailWrap}>
        <Box className={classes.bigImageWrap}>
          <img src={profileImage?.photoUrl} className={classes.bigImage} />
        </Box>
        <Grid container className={classes.detailContentWrap}>
          {/* User id */}
          <Grid item>
            <Grid container className={classes.gridRow}>
              <Grid item className={classes.gridTitle}>
                User ID
              </Grid>
              <Grid item className={classes.gridContent}>
                <PersonIcon className={classes.personIcon} />
                {userId && (
                  <Link href={`${settings.getCrewURI()}/users/${userId}`} target="_blank" className={classes.contentLink}>
                    {userId}
                  </Link>
                )}
              </Grid>
            </Grid>
          </Grid>
          {/* User name */}
          <Grid item>
            <Grid container className={classes.gridRow}>
              <Grid item className={classes.gridTitle}>
                User name
              </Grid>
              <Grid item className={classes.gridContent}>
                <span className={classes.gridUsername}>{username}</span>
              </Grid>
            </Grid>
          </Grid>
          {/* Display Name */}
          <Grid item>
            <Grid container className={classes.gridRow}>
              <Grid item className={classes.gridTitle}>
                Display name
              </Grid>
              <Grid item className={classes.gridContent}>
                {isWriter && (
                  <Typography className={classes.contentWriter}>
                    <span className={classes.crownIcon}>
                      <CrownIcon color={settings.getThemeColor()} scale={1.5} />
                    </span>
                    <span>{displayName}</span>
                  </Typography>
                )}
                {!isWriter && <span className={classes.gridUsername}>{displayName}</span>}
              </Grid>
            </Grid>
          </Grid>

          {/* Created At */}
          <Grid item>
            <Grid container className={classes.gridRow}>
              <Grid item className={classes.gridTitle}>
                Created at
              </Grid>
              <Grid item className={classes.gridContent}>
                {moment(createdAt).format('ll HH:mm:ss')}
              </Grid>
            </Grid>
          </Grid>
          {/* Status */}
          <Grid item>
            <Grid container className={classes.gridRow}>
              <Grid item className={classes.gridTitle}>
                Status
              </Grid>
              <Grid
                item
                className={cx({
                  [classes.gridContent]: true,
                  [classes.approve]: reviewStatus === ProfileImageReviewStatus.Approved,
                  [classes.fatal]: reviewStatus === ProfileImageReviewStatus.Blocked,
                })}
              >
                <span className={classes.reviewStatus}>{reviewStatus?.replace('_', ' ')}</span>
              </Grid>
            </Grid>
          </Grid>
          {/* Reported Count */}
          <Grid item>
            <Grid container className={classes.gridRow}>
              <Grid item className={classes.gridTitle}>
                <span className={classes.fatal}>Reported</span>
              </Grid>
              <Grid item className={classes.gridContent}>
                <span className={classes.fatal}>{reportedCount}</span>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.historiesWrap}>
        <Typography className={classes.subtitle}>Updated history</Typography>
        <TablePagination
          count={totalCount}
          page={page}
          rowsPerPage={RowsPerPage}
          rowsPerPageOptions={[]}
          onChangePage={handlePageChange}
          backIconButtonProps={{
            className: classes.paginationIconButton,
          }}
          nextIconButtonProps={{
            className: classes.paginationIconButton,
          }}
          classes={{
            toolbar: classes.paginationToolbar,
          }}
        />
        <Box className={classes.historyItemsWrap}>
          <Box className={classes.historyItemsInnerWrap}>
            {updatedHistories.map(h => (
              <Box key={h.id} className={classes.historyItem}>
                <Box className={classes.smallImageWrap}>
                  <img src={h.photoUrl} className={classes.smallImage} />
                </Box>
                <Box className={classes.historyContent}>
                  <Typography className={classes.dateTime}>{`Updated ${moment(h.review.updatedAt).format('ll HH:mm:ss')}`}</Typography>
                  <Typography
                    className={cx({
                      [classes.gridContent]: true,
                      [classes.approve]: h.review.status === ProfileImageReviewStatus.Approved,
                      [classes.fatal]: h.review.status === ProfileImageReviewStatus.Blocked,
                    })}
                  >
                    <span className={classes.reviewStatus}>{h.review.status.replace('_', ' ')}</span>
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box className={classes.buttonWrap}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          style={{
            textTransform: 'uppercase',
          }}
        >
          Close
        </Button>
      </Box>
    </Paper>
  );
};

export default ProfileImageDetail;
